import { useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Empty, PageHeader, Panel } from "@/components/admin/AdminUI";
import DirectionCard from "@/components/admin/discover/DirectionCard";
import MemberPanel from "@/components/admin/discover/MemberPanel";
import { useAdmin } from "@/admin/AuthContext";
import { api } from "@/lib/api";

// Owner only: GET /admin/diagnostics/discover?viewer=&target= (phone or user id each). Every call is audited by the server.
export default function DiscoverCheck() {
  const owner = useAdmin().admin?.role === "owner";
  const [f, setF] = useState({ viewer: "", target: "" });
  const [res, setRes] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const run = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const q = new URLSearchParams({ viewer: f.viewer.trim(), target: f.target.trim() });
      setRes(await api(`/admin/diagnostics/discover?${q}`));
    } catch (err) {
      setRes(null);
      setError(err.status === 403 ? "Only the Owner can run this check." : err.message);
    } finally { setBusy(false); }
  };

  if (!owner) return <div data-testid="discover-check-page"><PageHeader title="Discover check" /><Empty title="Owner only" text="Only the Owner can run the Discover check." testId="discover-check-forbidden" /></div>;
  const a = res?.viewer?.name || "A", b = res?.target?.name || "B";
  return (
    <div className="space-y-6" data-testid="discover-check-page">
      <PageHeader title="Discover check" description="Why can't they see each other? Compare two members both ways." />
      <Panel className="p-6">
        <form onSubmit={run} className="grid items-end gap-4 sm:grid-cols-[1fr_1fr_auto]">
          <div><Label htmlFor="dc-viewer">Member A</Label>
            <Input id="dc-viewer" value={f.viewer} onChange={set("viewer")} placeholder="Phone (+1…) or user ID" className="mt-2 font-mono" autoComplete="off" data-testid="discover-check-viewer-input" /></div>
          <div><Label htmlFor="dc-target">Member B</Label>
            <Input id="dc-target" value={f.target} onChange={set("target")} placeholder="Phone (+1…) or user ID" className="mt-2 font-mono" autoComplete="off" data-testid="discover-check-target-input" /></div>
          <Button type="submit" disabled={busy || !f.viewer.trim() || !f.target.trim()} data-testid="discover-check-submit-button"><MagnifyingGlass />{busy ? "Checking…" : "Check"}</Button>
        </form>
        {error && <p className="cw-tone-error mt-4 rounded-xl px-3 py-2 text-sm" role="alert" data-testid="discover-check-error">{error}</p>}
      </Panel>
      {res && (
        <div className="space-y-6" data-testid="discover-check-result">
          <div className="grid gap-6 xl:grid-cols-2">
            <DirectionCard title={`${a} sees ${b}`} result={res.viewer_sees_target} testId="discover-check-a-sees-b" />
            <DirectionCard title={`${b} sees ${a}`} result={res.target_sees_viewer} testId="discover-check-b-sees-a" />
          </div>
          <div className="grid gap-6 xl:grid-cols-2">
            <MemberPanel who="Member A" m={res.viewer} testId="discover-check-member-a" />
            <MemberPanel who="Member B" m={res.target} testId="discover-check-member-b" />
          </div>
        </div>
      )}
    </div>
  );
}
